import { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { smallScreenSize, useContainerQuery } from '../../hooks';
import {
  Button,
  Chip,
  ContainerAvatar,
  InputContainer,
  Paper,
  SearchableSelect,
  Typography,
} from '../../design-system';
import { i18n } from '../../i18n';
import { containersService } from '../../services';
import {
  extendContainerValidator,
  formatContainerType,
  momentDiffDays,
  momentTextFormat,
} from '../../utils';
import {
  ContainerConditionToChipColors,
  ContainerConditionToChipLabels,
  ContainerStatusToChipColors,
  ContainerStatusToChipLabels,
  TABLE,
} from '../../const';
import { useStore } from '../../context';
import type { ContainerType } from '../../types';

export const ThreeDPlacesPage = styled.div`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: 3rem;

  height: 100%;

  @media (max-width: ${smallScreenSize}px) {
    grid-row-gap: var(--spacing-3);
  }
`;

const EmptySlotContainerPaper = styled(Paper)`
  position: absolute;
  top: var(--spacing-4);
  left: var(--spacing-4);
  z-index: 1;

  display: grid;
  grid-template-columns: 25rem;
  grid-auto-flow: row;
  gap: var(--spacing-6);
  align-items: end;
  width: max-content;
  height: auto;
`;

const HOVER_MENU_OFFSET = 12;

const SlotHoverInfoPaper = styled(Paper)`
  display: grid;
  gap: var(--spacing-2);
  width: max-content;
  height: auto;

  padding: var(--spacing-2) !important;
`;

const HoverInfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-6);
  min-height: 3.2rem;
  padding: var(--spacing-2) var(--spacing-3);
  border-radius: 0.4rem;
  background: var(--gray-30);
`;

type SceneHoverMenuItem = {
  label: string;
  value: string | number;
};

type SceneHoverMenuProps = {
  items: SceneHoverMenuItem[];
  x: number;
  y: number;
};

const SceneHoverMenu = ({ items, x, y }: SceneHoverMenuProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const parent = wrapper?.offsetParent as HTMLElement | null;
    if (!wrapper || !parent) return;

    const { offsetWidth: width, offsetHeight: height } = wrapper;
    let translateX = HOVER_MENU_OFFSET;
    let translateY = HOVER_MENU_OFFSET;

    if (x + HOVER_MENU_OFFSET + width > parent.clientWidth) {
      translateX = -(width + HOVER_MENU_OFFSET);
    }

    if (y + HOVER_MENU_OFFSET + height > parent.clientHeight) {
      translateY = -(height + HOVER_MENU_OFFSET);
    }

    wrapper.style.transform = `translate(${translateX}px, ${translateY}px)`;
  }, [items, x, y]);

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'absolute',
        left: x,
        top: y,
        zIndex: 2,
        pointerEvents: 'none',
        transform: `translate(${HOVER_MENU_OFFSET}px, ${HOVER_MENU_OFFSET}px)`,
      }}
    >
      <SlotHoverInfoPaper paperSize="max-content">
        {items.map((item) => (
          <HoverInfoRow key={item.label}>
            <Typography variant="textS" weight="medium">
              {item.label}
            </Typography>
            <Typography variant="textS">{item.value}</Typography>
          </HoverInfoRow>
        ))}
      </SlotHoverInfoPaper>
    </div>
  );
};

type SlotHoverInfoProps = {
  place: string;
  tier: number;
  x: number;
  y: number;
};

export const SlotHoverInfo = ({ place, tier, x, y }: SlotHoverInfoProps) => (
  <SceneHoverMenu
    x={x}
    y={y}
    items={[
      { label: i18n.t('label.place'), value: place },
      { label: i18n.t('label.tier'), value: tier },
    ]}
  />
);

type ContainerHoverInfoProps = {
  number: string;
  type?: ContainerType;
  clientCode?: string;
  place: string;
  tier: number;
  x: number;
  y: number;
};

export const ContainerHoverInfo = ({
  number,
  type,
  clientCode,
  place,
  tier,
  x,
  y,
}: ContainerHoverInfoProps) => (
  <SceneHoverMenu
    x={x}
    y={y}
    items={[
      { label: i18n.t('label.containerNumber'), value: number },
      { label: i18n.t('label.type'), value: type ? formatContainerType(type) : '—' },
      { label: i18n.t('label.client'), value: clientCode || '—' },
      { label: i18n.t('label.place'), value: place },
      { label: i18n.t('label.tier'), value: tier },
    ]}
  />
);

type EmptySlotContainerSelectProps = {
  yardId: string;
  hasSelectedSlot?: boolean;
  sceneContainers?: { id: string; number: string }[];
  onAddContainer?: (container: { id: string; number: string; length: number }) => void;
  onSceneContainerSelect?: (container: { id: string; number: string }) => void;
  onClearSceneContainerSelect?: () => void;
};

export const EmptySlotContainerSelect = ({
  yardId,
  hasSelectedSlot = false,
  sceneContainers = [],
  onAddContainer,
  onSceneContainerSelect,
  onClearSceneContainerSelect,
}: EmptySlotContainerSelectProps) => {
  const [selectedContainer, setSelectedContainer] = useState<{
    id: string;
    number: string;
    length: number;
  }>();

  if (!yardId) return null;

  const isSelectedInScene =
    !!selectedContainer && sceneContainers.some((item) => item.id === selectedContainer.id);

  return (
    <EmptySlotContainerPaper paperSize="max-content">
      <SearchableSelect
        InputComponent={InputContainer}
        label={i18n.t('label.containerNumber')}
        selectedOptionLabel={
          selectedContainer?.number || i18n.t('placeholder.selectContainerNumber')
        }
        queryKey={['container-short', yardId]}
        queryFn={(number: string) => async () => {
          const params: any = {
            yardId,
            number,
            take: TABLE.PAGE_MAX_SIZE,
            skip: 0,
            short: true,
            hasLocation: 'all',
            notLoading: true,
          };
          if (
            typeof params.number === 'string' &&
            (params.number?.length < 3 || !extendContainerValidator(params.number))
          )
            delete params.number;

          const { data } = await containersService.getAll({
            params,
          });

          return data.data.map((container) => ({
            id: container.id,
            label: container.number,
            length: container.type.startsWith('20') ? 20 : 40,
          }));
        }}
        selectedAreasEqaul={{ key: 'id', keys: selectedContainer ? [selectedContainer.id] : [] }}
        onSelect={(container) => {
          const nextContainer = container
            ? {
                id: container.id,
                number: container.label,
                length: container.length,
              }
            : undefined;

          setSelectedContainer(nextContainer);

          if (!nextContainer) {
            onClearSceneContainerSelect?.();
            return;
          }

          const existsInScene = sceneContainers.some((item) => item.id === nextContainer.id);

          if (existsInScene) {
            onSceneContainerSelect?.({ id: nextContainer.id, number: nextContainer.number });
            return;
          }

          onClearSceneContainerSelect?.();
        }}
      />
      <Button
        size="medium"
        disabled={!selectedContainer || !hasSelectedSlot || isSelectedInScene}
        onClick={() => selectedContainer && onAddContainer?.(selectedContainer)}
      >
        {i18n.t('button.save')}
      </Button>
    </EmptySlotContainerPaper>
  );
};

const ContainerDetailsPaper = styled(EmptySlotContainerPaper)`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-3);
`;

const ContainerDetailsHeader = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: var(--spacing-3);
  align-items: center;
`;

const ContainerDetailsHeaderText = styled.div`
  display: grid;
  gap: var(--spacing-1);
`;

const ContainerDetail = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-6);
  min-height: 3.2rem;
  padding: var(--spacing-2) var(--spacing-3);
  border-radius: 0.4rem;
  background: var(--gray-30);
`;

type ContainerDetailsProps = {
  containerId: string;
  onClose: () => void;
  handleClickImage: (images: { url: string }[]) => void;
  onDelete?: () => void;
};

export const ContainerDetails = ({
  containerId,
  onClose,
  handleClickImage,
  onDelete,
}: ContainerDetailsProps) => {
  const { data: container, isFetching } = useContainerQuery(containerId);
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  if (isFetching || !container?.id) return null;

  const marketStatus = container.isHold
    ? 'HOLD'
    : container.sale?.status
    ? container.sale.status
    : undefined;
  const status =
    container.status === 'GATE_OUT_REQUESTED' && container.gateOut?.status === 'EXPIRED'
      ? 'GATE_OUT_EXPIRED'
      : container.status;
  const condition = container.condition as keyof typeof ContainerConditionToChipLabels | undefined;
  const typedMarketStatus = marketStatus as keyof typeof ContainerStatusToChipLabels | undefined;
  const typedStatus = status as keyof typeof ContainerStatusToChipLabels;

  return (
    <ContainerDetailsPaper paperSize="max-content" onClose={onClose}>
      <ContainerDetailsHeader>
        <ContainerAvatar
          images={container.images ?? []}
          size="medium"
          onClick={() => {
            if (container.images?.length) handleClickImage(container.images);
          }}
        />
        <ContainerDetailsHeaderText>
          <Typography variant="textL" weight="bold">
            {container.number}
          </Typography>
          <Typography variant="textS">{container.clientYard?.client?.code || '-'}</Typography>
        </ContainerDetailsHeaderText>
      </ContainerDetailsHeader>
      <ContainerDetail>
        <Typography variant="textS">{i18n.t('label.type')}</Typography>
        <Typography variant="textS">{formatContainerType(container.type)}</Typography>
      </ContainerDetail>
      <ContainerDetail>
        <Typography variant="textS">{i18n.t('label.condition')}</Typography>
        {condition ? (
          <Chip
            size="small"
            label={ContainerConditionToChipLabels[condition]}
            color={ContainerConditionToChipColors[condition]}
          />
        ) : (
          <Typography variant="textS">-</Typography>
        )}
      </ContainerDetail>
      <ContainerDetail>
        <Typography variant="textS">{i18n.t('label.marketStatus')}</Typography>
        {typedMarketStatus ? (
          <Chip
            size="small"
            label={ContainerStatusToChipLabels[typedMarketStatus]}
            color={ContainerStatusToChipColors[typedMarketStatus]}
          />
        ) : (
          <Typography variant="textS">-</Typography>
        )}
      </ContainerDetail>
      <ContainerDetail>
        <Typography variant="textS">{i18n.t('label.status')}</Typography>
        <Chip
          size="small"
          label={ContainerStatusToChipLabels[typedStatus]}
          color={ContainerStatusToChipColors[typedStatus]}
        />
      </ContainerDetail>
      <ContainerDetail>
        <Typography variant="textS">{i18n.t('label.gateInDate')}</Typography>
        <Typography variant="textS">
          {container.gateIn?.completedAt
            ? momentTextFormat(container.gateIn.completedAt, user)
            : '-'}
        </Typography>
      </ContainerDetail>
      <ContainerDetail>
        <Typography variant="textS">{i18n.t('label.daysInTerminal')}</Typography>
        <Typography variant="textS">
          {momentDiffDays(
            container.gateIn?.completedAt,
            container.gateOut?.completedAt || new Date(),
          )}{' '}
          {i18n.t('pages.containers.table.days')}
        </Typography>
      </ContainerDetail>
      {onDelete && (
        <Button size="medium" data-color="destructive" onClick={onDelete}>
          {i18n.t('button.delete')}
        </Button>
      )}
    </ContainerDetailsPaper>
  );
};
